import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { facilityAPI } from '../../api/axios';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import {
  ArrowLeft, LogOut, Plus, Building2, Pencil, Trash2,
  Loader2, ChevronRight, MapPin, Users, Clock, Layers
} from 'lucide-react';

const inp = "w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 bg-gray-50";
const sel = inp;

const FACILITY_TYPES = [
  { value: 'HOSPITAL',         label: 'Hospital' },
  { value: 'CLINIC',           label: 'Clinic' },
  { value: 'HOME',             label: 'Home Care' },
  { value: 'REHAB_CENTER',     label: 'Rehab Center' },
  { value: 'CORPORATE_OFFICE', label: 'Corporate / Wellness' },
  { value: 'RESIDENCE',        label: 'Individual Residence' },
];

const CARE_LEVELS = ['BASIC', 'MODERATE', 'INTENSIVE'];
const DEPT_OPTIONS = ['ICU', 'General Ward', 'OPD', 'Emergency', 'Maternity',
  'Pediatrics', 'Orthopedics', 'Cardiology', 'Neurology', 'Oncology'];

const emptyForm = {
  facilityName: '', facilityType: 'HOSPITAL',
  bedCount: '', departmentCount: '', departments: '[]',
  operatingHours: '', shiftCount: '', currentStaffCount: '',
  patientCount: '', careLevel: '', specialRequirements: '',
  addressLine: '', city: '', state: '', pincode: '',
};

export default function MyFacilities() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [selectedDepts, setSelectedDepts] = useState([]);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const res = await facilityAPI.list();
      setFacilities(res.data.data || []);
    } catch { toast.error('Failed to load facilities'); }
    finally { setLoading(false); }
  };

  const openAdd = () => {
    setForm(emptyForm); setSelectedDepts([]); setEditId(null); setShowForm(true);
  };

  const openEdit = (f) => {
    setForm({
      facilityName: f.facilityName || '', facilityType: f.facilityType || 'HOSPITAL',
      bedCount: f.bedCount || '', departmentCount: f.departmentCount || '',
      departments: f.departments || '[]',
      operatingHours: f.operatingHours || '', shiftCount: f.shiftCount || '',
      currentStaffCount: f.currentStaffCount || '', patientCount: f.patientCount || '',
      careLevel: f.careLevel || '', specialRequirements: f.specialRequirements || '',
      addressLine: f.addressLine || '', city: f.city || '',
      state: f.state || '', pincode: f.pincode || '',
    });
    try { setSelectedDepts(JSON.parse(f.departments || '[]')); } catch { setSelectedDepts([]); }
    setEditId(f.id); setShowForm(true);
  };

  const toggleDept = (dept) => {
    const updated = selectedDepts.includes(dept)
      ? selectedDepts.filter(d => d !== dept)
      : [...selectedDepts, dept];
    setSelectedDepts(updated);
    setForm(f => ({ ...f, departments: JSON.stringify(updated) }));
  };

  const handleSave = async () => {
    if (!form.facilityName.trim()) { toast.error('Facility name is required'); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      // Convert numeric strings to numbers
      ['bedCount','departmentCount','shiftCount','currentStaffCount','patientCount']
        .forEach(k => { if (payload[k] !== '') payload[k] = parseInt(payload[k]); else delete payload[k]; });

      if (editId) {
        await facilityAPI.update(editId, payload);
        toast.success('Facility updated!');
      } else {
        await facilityAPI.add(payload);
        toast.success('Facility added!');
      }
      setShowForm(false);
      load();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this facility?')) return;
    try {
      await facilityAPI.remove(id);
      setFacilities(f => f.filter(x => x.id !== id));
      toast.success('Facility removed');
    } catch { toast.error('Failed to remove'); }
  };

  const ftypeLabel = (v) => FACILITY_TYPES.find(t => t.value === v)?.label || v;
  const needsBeds = ['HOSPITAL', 'CLINIC', 'REHAB_CENTER'].includes(form.facilityType);
  const needsCareLevel = ['HOME', 'RESIDENCE'].includes(form.facilityType);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <Loader2 className="h-10 w-10 animate-spin text-teal-600" />
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navbar */}
      <nav className="bg-white border-b border-gray-100 shadow-sm sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/company/dashboard')}
              className="text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100 transition">
              <ArrowLeft className="h-5 w-5" />
            </button>
            <img src="/logo-horizontal.png" alt="ShramSena" className="h-7 w-auto" />
            <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-lg text-xs font-bold uppercase">Company</span>
          </div>
          <button onClick={() => { logout(); navigate('/company/login'); }}
            className="flex items-center gap-1.5 text-sm font-medium text-red-500 hover:text-red-700">
            <LogOut className="h-4 w-4" /> Logout
          </button>
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-xl font-bold text-slate-800">My Facilities</h1>
            <p className="text-sm text-gray-400 mt-0.5">
              Each facility is used to generate an AI Staffing Plan
            </p>
          </div>
          <button onClick={openAdd}
            className="flex items-center gap-2 bg-teal-600 text-white px-5 py-2.5 rounded-xl
              font-bold text-sm hover:bg-teal-700 transition shadow-lg shadow-teal-500/20">
            <Plus className="h-4 w-4" /> Add Facility
          </button>
        </div>

        {/* List */}
        {facilities.length === 0 ? (
          <div className="text-center py-24 bg-white rounded-2xl border border-gray-100 shadow-sm">
            <Building2 className="h-14 w-14 text-gray-200 mx-auto mb-4" />
            <h3 className="font-bold text-gray-400 text-lg">No facilities yet</h3>
            <p className="text-sm text-gray-400 mt-1 mb-6">
              Add your first facility to generate an AI Staffing Plan
            </p>
            <button onClick={openAdd}
              className="bg-teal-600 text-white px-6 py-2.5 rounded-xl font-bold text-sm hover:bg-teal-700 transition">
              Add Facility
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {facilities.map(f => (
              <div key={f.id}
                className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-4 flex-1">
                    <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center shrink-0">
                      <Building2 className="h-6 w-6 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-bold text-slate-800">{f.facilityName}</h3>
                        <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded-lg text-xs font-semibold">
                          {ftypeLabel(f.facilityType)}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-x-5 gap-y-1 mt-2 text-xs text-gray-500">
                        {f.city && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {f.city}{f.state ? `, ${f.state}` : ''}</span>}
                        {f.bedCount && <span className="flex items-center gap-1"><Layers className="h-3 w-3" /> {f.bedCount} beds</span>}
                        {f.shiftCount && <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {f.shiftCount} shifts</span>}
                        {f.currentStaffCount != null && <span className="flex items-center gap-1"><Users className="h-3 w-3" /> {f.currentStaffCount} current staff</span>}
                        {f.operatingHours && <span>{f.operatingHours}</span>}
                      </div>
                      {f.departments && f.departments !== '[]' && (() => {
                        try {
                          const depts = JSON.parse(f.departments);
                          return depts.length > 0 ? (
                            <div className="flex flex-wrap gap-1.5 mt-2">
                              {depts.map(d => (
                                <span key={d} className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">{d}</span>
                              ))}
                            </div>
                          ) : null;
                        } catch { return null; }
                      })()}
                    </div>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button onClick={() => navigate(`/company/staffing-plans/generate?facilityId=${f.id}`)}
                      className="flex items-center gap-1.5 bg-teal-600 text-white px-4 py-2 rounded-xl text-xs font-bold hover:bg-teal-700 transition shadow-sm">
                      Generate Plan <ChevronRight className="h-3 w-3" />
                    </button>
                    <button onClick={() => openEdit(f)}
                      className="p-2 text-gray-400 hover:text-teal-600 hover:bg-teal-50 rounded-xl transition">
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button onClick={() => handleDelete(f.id)}
                      className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 px-4 py-8 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl p-7 my-auto">
            <h2 className="font-bold text-slate-800 text-lg mb-6">
              {editId ? 'Edit Facility' : 'Add New Facility'}
            </h2>

            <div className="space-y-5">
              {/* Basic */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <label className="block text-xs font-bold text-slate-700 mb-1.5">Facility Name *</label>
                  <input value={form.facilityName}
                    onChange={e => setForm(f => ({ ...f, facilityName: e.target.value }))}
                    placeholder="City General Hospital – Main Building"
                    className={inp} />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1.5">Facility Type *</label>
                  <select value={form.facilityType}
                    onChange={e => setForm(f => ({ ...f, facilityType: e.target.value }))}
                    className={sel}>
                    {FACILITY_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1.5">Operating Hours</label>
                  <input value={form.operatingHours}
                    onChange={e => setForm(f => ({ ...f, operatingHours: e.target.value }))}
                    placeholder="24x7 or 8AM–8PM" className={inp} />
                </div>
              </div>

              {/* Hospital/Clinic specific */}
              {needsBeds && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  {[
                    { label: 'Bed Count',          key: 'bedCount',          placeholder: '120' },
                    { label: 'Departments',         key: 'departmentCount',   placeholder: '5' },
                    { label: 'Shifts per Day',      key: 'shiftCount',        placeholder: '3' },
                    { label: 'Current Staff',       key: 'currentStaffCount', placeholder: '45' },
                  ].map(f => (
                    <div key={f.key}>
                      <label className="block text-xs font-bold text-slate-700 mb-1.5">{f.label}</label>
                      <input type="number" value={form[f.key]}
                        onChange={e => setForm(prev => ({ ...prev, [f.key]: e.target.value }))}
                        placeholder={f.placeholder} className={inp} />
                    </div>
                  ))}
                </div>
              )}

              {/* Patient count */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1.5">
                    {needsBeds ? 'Daily Patients' : 'Clients / Residents'}
                  </label>
                  <input type="number" value={form.patientCount}
                    onChange={e => setForm(f => ({ ...f, patientCount: e.target.value }))}
                    placeholder={needsBeds ? '80' : '15'} className={inp} />
                </div>
                {!needsBeds && (
                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1.5">Shifts per Day</label>
                    <input type="number" value={form.shiftCount}
                      onChange={e => setForm(f => ({ ...f, shiftCount: e.target.value }))}
                      placeholder="2" className={inp} />
                  </div>
                )}
                {needsCareLevel && (
                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1.5">Care Level</label>
                    <select value={form.careLevel}
                      onChange={e => setForm(f => ({ ...f, careLevel: e.target.value }))}
                      className={sel}>
                      <option value="">Select</option>
                      {CARE_LEVELS.map(l => <option key={l} value={l}>{l}</option>)}
                    </select>
                  </div>
                )}
              </div>

              {/* Departments multi-select */}
              {needsBeds && (
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-2">Departments</label>
                  <div className="flex flex-wrap gap-2">
                    {DEPT_OPTIONS.map(d => (
                      <button key={d} type="button" onClick={() => toggleDept(d)}
                        className={`px-3 py-1.5 rounded-xl text-xs font-semibold border transition
                          ${selectedDepts.includes(d)
                            ? 'bg-teal-600 text-white border-teal-600'
                            : 'bg-white text-gray-600 border-gray-200 hover:border-teal-400'}`}>
                        {d}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Special requirements */}
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">
                  Special Requirements
                </label>
                <textarea value={form.specialRequirements}
                  onChange={e => setForm(f => ({ ...f, specialRequirements: e.target.value }))}
                  placeholder="Any specific certifications, languages, or conditions required..."
                  rows={2}
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm bg-gray-50 focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none" />
              </div>

              {/* Address */}
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-2">
                  Facility Location <span className="text-gray-400 font-normal">(if different from company address)</span>
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="sm:col-span-3">
                    <input value={form.addressLine}
                      onChange={e => setForm(f => ({ ...f, addressLine: e.target.value }))}
                      placeholder="Street address" className={inp} />
                  </div>
                  <input value={form.city}
                    onChange={e => setForm(f => ({ ...f, city: e.target.value }))}
                    placeholder="City" className={inp} />
                  <input value={form.state}
                    onChange={e => setForm(f => ({ ...f, state: e.target.value }))}
                    placeholder="State" className={inp} />
                  <input value={form.pincode}
                    onChange={e => setForm(f => ({ ...f, pincode: e.target.value }))}
                    placeholder="Pincode" maxLength={6} className={inp} />
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-7">
              <button onClick={() => setShowForm(false)}
                className="flex-1 py-2.5 border border-gray-200 rounded-xl text-sm font-semibold text-gray-600 hover:bg-gray-50">
                Cancel
              </button>
              <button onClick={handleSave} disabled={saving}
                className="flex-1 py-2.5 bg-teal-600 text-white rounded-xl text-sm font-bold hover:bg-teal-700 disabled:opacity-60 flex items-center justify-center gap-2">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {editId ? 'Save Changes' : 'Add Facility'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
